/**
 * ตัวแดง / ตัวเหลือง (duel armour, lane DUEL-ARMOR; data/duelArmor.ts; design docs/design/combat/2026-10-11-duel-armor.md).
 * In a bout's fight (`DUEL_FLAGS.v2`); the open world keeps the legacy `SkillDef.armour` (a red wind-up, World.applyHitNow).
 *
 * - The tier is read live from the fighter: a wind-up under way (`cast`) or a spin (`spinT`) of an armoured move, within the
 *   window's cap (`DUEL_ARMOR.redMax` / `yellowMax`). Never in a recovery (the move is over by then), never on a break.
 * - Red: the World's own `armoured` path (through `World.armourHook`) keeps the cast through stuns and interrupts and drops
 *   the stun; the bout's shove is held (`shoveHeld`); damage lands in full.
 * - Yellow: shoves are held; a stun still lands (the World cancels the move, and with it the armour); a blow it soaks costs
 *   `yellowCost` พลังกาย, and short of that the armour breaks (that blow staggers `breakStagger`, armour off for this use);
 *   a landed break and the pierce moves (`ARMOR_PIERCE`) stun it (`pierceStun`). Flings and trips on yellow are systems/duelKnock.ts's (a short stun).
 * - What it saw (for the client and the tests): the latest soak and break per fighter, and a tally.
 * Deterministic: no draws; time is the World's.
 */
import { ARMOR_PIERCE, DUEL_ARMOR, armorWindowsOf, type ArmorTier } from '../data/duelArmor';
import { DUEL_FLAGS } from '../data/duelMatch';
import { SKILLS } from '../data/skills';
import type { Entity } from '../world';
import { addShoveHold } from './duelHolds';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelArmorSystem>();
export const duelArmorOf = (w: object): DuelArmorSystem | null => SYSTEMS.get(w) ?? null;

export interface ArmorView {
  readonly tier: ArmorTier;
  /** Seconds the window still runs (at most; the move may end sooner). */
  readonly left: number;
  /** The move it rides on. */
  readonly skillId: string;
}

export class DuelArmorSystem implements WorldSystem {
  readonly name = 'duelArmor';
  /** Fighter id -> the move whose yellow armour broke this use (off until that move is over). */
  private readonly broken = new Map<number, string>();
  /** Fighter id -> world time of the latest blow soaked, and of the latest break (the client's spark and shards). */
  readonly soakedAt = new Map<number, number>();
  readonly brokeAt = new Map<number, number>();
  /** Counts, for the tests and the sims. */
  readonly tally = { red: 0, yellow: 0, breaks: 0, pierced: 0 };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    const w = this.h.world;
    w.armourHook = (e) => this.tier(e.id) === 'red';
    addShoveHold(w, (id) => this.tier(id) !== null);
    const prev = w.duelMod;
    w.duelMod = (src, tgt, res, kind, skillId) => {
      prev?.(src, tgt, res, kind, skillId);
      this.after(tgt, res, kind, skillId);
    };
  }

  private inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  /** The armour a fighter has this instant, with the window left (null: none). */
  view(id: number): ArmorView | null {
    if (!this.inBout(id)) return null;
    const e = this.h.world.get(id);
    if (!e || e.dead) return null;
    if (e.cast) {
      const win = armorWindowsOf(e.cast.skillId).find((x) => x.phase === 'windup');
      if (win && SKILLS[e.cast.skillId]?.kind !== 'break') {
        const cap = win.tier === 'red' ? DUEL_ARMOR.redMax : DUEL_ARMOR.yellowMax;
        const left = Math.min(e.cast.windup - e.cast.t, cap - e.cast.t);
        if (left > 0 && !(win.tier === 'yellow' && this.broken.get(id) === e.cast.skillId))
          return { tier: win.tier, left, skillId: e.cast.skillId };
      }
    }
    if (e.spinT > 0 && e.spinSkill) {
      const win = armorWindowsOf(e.spinSkill).find((x) => x.phase === 'spin');
      const dur = SKILLS[e.spinSkill]?.spin?.duration ?? 0;
      if (win) {
        const cap = win.tier === 'red' ? DUEL_ARMOR.redMax : DUEL_ARMOR.yellowMax;
        const ran = dur - e.spinT;
        const left = Math.min(e.spinT, cap - ran);
        if (left > 0 && !(win.tier === 'yellow' && this.broken.get(id) === e.spinSkill))
          return { tier: win.tier, left, skillId: e.spinSkill };
      }
    }
    return null;
  }

  tier(id: number): ArmorTier | null {
    return this.view(id)?.tier ?? null;
  }

  /** After the bout's rules, before the World applies the blow. */
  private after(
    tgt: Entity,
    res: { outcome: string; damage: number; stun: number; spent?: boolean },
    kind: string,
    skillId: string,
  ): void {
    const a = this.view(tgt.id);
    if (!a) return;
    const landed = (res.outcome === 'hit' || res.outcome === 'interrupt') && res.damage > 0 && !res.spent;
    if (!landed) return;
    const now = this.h.world.time;
    if (a.tier === 'red') {
      res.stun = 0;
      this.tally.red++;
      this.soakedAt.set(tgt.id, now);
      return;
    }
    // Yellow: a stun gets through (the World ends the move); a landed break and the pierce moves bring their own.
    if (res.stun <= 0 && (kind === 'break' || ARMOR_PIERCE.includes(skillId))) {
      res.stun = DUEL_ARMOR.pierceStun;
      this.tally.pierced++;
    }
    if (res.stun > 0 || (SKILLS[skillId]?.hitStun ?? 0) > 0) return;
    if (tgt.stamina >= DUEL_ARMOR.yellowCost) {
      tgt.stamina -= DUEL_ARMOR.yellowCost;
      this.tally.yellow++;
      this.soakedAt.set(tgt.id, now);
      return;
    }
    // Out of breath: the armour breaks, this blow lands in full.
    this.broken.set(tgt.id, a.skillId);
    res.stun = Math.max(res.stun, DUEL_ARMOR.breakStagger);
    this.tally.breaks++;
    this.brokeAt.set(tgt.id, now);
  }

  step(_dt: number): void {
    // A broken armour is off until its move is over.
    for (const [id, skillId] of [...this.broken]) {
      const e = this.h.world.get(id);
      if (!e || (e.cast?.skillId !== skillId && e.spinSkill !== skillId)) this.broken.delete(id);
    }
  }
}
